/** Shared Outpost16 types — minimal scaffold. */

export type MemberRole = "owner" | "member" | "viewer";

export interface Org {
  id: string;
  name: string;
  createdAt: string;
}

export interface ProductStrategy {
  selectedChannels?: string[];
  deferredChannels?: string[];
  desiredOutcomes?: string[];
  reviewHorizon?: string | null;
  currentGoalId?: string | null;
}

export interface Product {
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  url: string | null;
  logoR2Key: string | null;
  oneLiner?: string | null;
  icpHypothesis?: string | null;
  strategyJson?: string | null;
  createdAt: string;
}

export type GoalStatus = "active" | "paused" | "completed" | "cancelled";

export type OwnerKind = "human" | "agent";

export type ExecutableBy = "human" | "agent" | "either";

export interface Goal {
  id: string;
  organizationId: string;
  productId: string | null;
  title: string;
  description: string | null;
  status: GoalStatus;
  targetJson: string | null;
  ownerKind: OwnerKind;
  createdAt: string;
}

export type QuestStatus =
  | "suggested"
  | "pending_approval"
  | "approved"
  | "accepted"
  | "in_progress"
  | "proof_submitted"
  | "completed"
  | "cancelled";

export type QuestLevel = 1 | 2 | 3 | 4;

export interface Quest {
  id: string;
  organizationId: string;
  productId: string | null;
  goalId: string | null;
  title: string;
  description: string | null;
  level: QuestLevel;
  status: QuestStatus;
  executableBy: ExecutableBy;
  assigneeKind: OwnerKind | null;
  assigneeTokenId: string | null;
  completedBy: OwnerKind | null;
  templateId?: string | null;
  templateVersion?: string | null;
  scopeJson?: string | null;
  incidentKey?: string | null;
  /** GTM mission pack id when created from a mission quest template. */
  missionId?: string | null;
  /** A0–A5 automation rung; independent of lookout level and API level. */
  automationLevel?: string | null;
  createdAt: string;
}

export type {
  AutomationLevel,
  AutomationLevelDef,
  MissionChannel,
  MissionDefinition,
  MissionProgressStatus,
  MissionQuestDefinition,
  ProductMissionProgress,
} from "./missions";

export {
  AUTOMATION_LEVEL_ORDER,
  GTM_MISSION_PACK_ID,
  GTM_MISSION_PACK_VERSION,
  automationAtLeast,
  automationRank,
  getMission,
  getMissionQuest,
  isAutomationLocked,
  listAutomationLevels,
  listMissionQuests,
  listMissions,
  listQuestsForMission,
  nextAutomationLevel,
} from "./missions";

export type TaskStatus =
  | "open"
  | "claimed"
  | "in_progress"
  | "blocked"
  | "done"
  | "cancelled";

export interface Task {
  id: string;
  organizationId: string;
  productId: string | null;
  questId: string | null;
  title: string;
  status: TaskStatus;
  ownerKind: OwnerKind;
  ownerTokenId: string | null;
  externalExecutorId: string | null;
  evidenceRef: string | null;
  metaJson: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgentActivityEvent {
  id: string;
  organizationId: string;
  action: string;
  actorAgentTokenId: string | null;
  agentName: string | null;
  questId: string | null;
  productId: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
}

export interface McpTokenPublic {
  id: string;
  name: string;
  scopes: string;
  createdAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
}

export interface XpAward {
  id: string;
  organizationId: string;
  questId: string | null;
  userId: string | null;
  amount: number;
  ruleVersion: string;
  evidenceId: string | null;
  createdAt: string;
}

export type AttentionSeverity = "info" | "attention" | "urgent";

export interface AttentionItem {
  id: string;
  organizationId: string;
  productId: string | null;
  title: string;
  summary: string | null;
  severity: AttentionSeverity;
  source: string | null;
  dismissedAt: string | null;
  snoozedUntil?: string | null;
  createdAt: string;
}

/** Community — demo personas are fixtures; follows/posts/comments are live. */
export type CommunityActorKind = "founder" | "demo";

export type CommunityPostKind = "update" | "ask" | "quest_complete";

export interface CommunityActor {
  id: string;
  kind: CommunityActorKind;
  displayName: string;
  startupName: string;
  startupId: string | null;
  oneLiner: string;
  bio: string;
  lookoutLevel: number;
  channels: string[];
  hook: string;
  productName: string;
  xpFlavor: number;
  avatarEmoji: string | null;
  /** R2 key for profile photo (prefer over emoji when set). */
  avatarR2Key: string | null;
  /** X/Twitter handle without @. */
  twitterHandle: string | null;
  joinedAt: string | null;
  /** When true (default), completed quests auto-post to the community feed. */
  autoShareQuestComplete?: boolean;
  /** When true (default), activity pops toasts and appears in Events. */
  alertsEnabled?: boolean;
  isYou?: boolean;
}

export type OrgEventSeverity = "info" | "attention" | "urgent";

export type OrgEventKind =
  | "follow.received"
  | "agent.progress"
  | "quest.completed"
  | "connector.connected"
  | "stripe.customer"
  | "stripe.payment"
  | string;

export interface OrgEvent {
  id: string;
  organizationId: string;
  kind: OrgEventKind;
  title: string;
  body: string | null;
  severity: OrgEventSeverity;
  source: string | null;
  productId: string | null;
  actorId: string | null;
  refType: string | null;
  refId: string | null;
  sharedToFeed: boolean;
  readAt: string | null;
  dismissedAt: string | null;
  createdAt: string;
}

export type CommunityBacklinkStatus =
  | "none"
  | "pending"
  | "verified"
  | "missing";

export interface CommunityStartup {
  id: string;
  founderActorId: string;
  founderDisplayName: string;
  founderLookoutLevel: number;
  name: string;
  tagline: string;
  about: string;
  url: string | null;
  stage: string;
  channels: string[];
  productName: string;
  productId: string | null;
  /** Reciprocal link to outpost16.com on their site (footer / Partners). */
  backlinkStatus: CommunityBacklinkStatus;
  backlinkCheckedAt: string | null;
  backlinkNote: string | null;
  joinedAt: string;
  /** Linked product logo in R2 (product directory). */
  logoR2Key: string | null;
}

export interface CommunityPost {
  id: string;
  actorId: string;
  displayName: string;
  startupName: string;
  actorKind: string;
  postKind: CommunityPostKind;
  lookoutLevel: number;
  body: string;
  meta: string | null;
  imageR2Key: string | null;
  createdAt: string;
  commentCount: number;
}

export interface CommunityComment {
  id: string;
  postId: string;
  actorId: string;
  displayName: string;
  kind: string;
  body: string;
  createdAt: string;
}

/** MCP token capability scopes (comma-separated on the token row). */
export const MCP_SCOPES = [
  "read",
  "write",
  "quests",
  "attention",
  "products",
  "tasks",
  "community",
] as const;

export type McpScope = (typeof MCP_SCOPES)[number];

export interface McpToken {
  id: string;
  organizationId: string;
  name: string;
  /** sha256 of token secret — never store raw */
  tokenHash: string;
  scopes: string;
  createdAt: string;
  revokedAt: string | null;
  lastUsedAt?: string | null;
}

/** Default scopes for a new agent token. */
export const MCP_DEFAULT_SCOPES =
  "read,write,attention,products,quests,tasks";

/**
 * Agent-facing MCP tools (BYOA contract).
 */
export const MCP_TOOLS = [
  "get_byoa_context",
  "get_product_brief",
  "get_struggle_digest",
  "list_attention",
  "dismiss_attention",
  "snooze_attention",
  "resolve_attention",
  "list_products",
  "get_product",
  "update_product",
  "get_product_strategy",
  "update_product_strategy",
  "list_quest_templates",
  "get_quest_template",
  "get_progression",
  "evaluate_quest_readiness",
  "list_capability_manifest",
  "list_goals",
  "suggest_goal",
  "accept_goal",
  "update_goal",
  "list_quests",
  "get_quest_progress",
  "suggest_quest",
  "accept_quest",
  "claim_quest",
  "attach_quest_proof",
  "submit_proof",
  "propose_quest_complete",
  "create_quest_from_template",
  "update_quest",
  "archive_quest",
  "get_evidence",
  "validate_quest_proof",
  "report_progress",
  "report_blocker",
  "list_customer_metrics",
  "get_lifecycle_summary",
  "query_product_metrics",
  "list_org_events",
  "dismiss_org_event",
  "list_channel_jobs",
  "list_ops_jobs",
  "get_ops_job",
  "list_job_executions",
  "report_job_execution",
  "get_product_integrations",
  "get_integration_config",
  "list_provider_resources",
  "update_product_integration",
  "request_integration_sync",
  "get_sync_status",
  "list_tasks",
  "claim_task",
  "update_task",
  "update_task_status",
  "attach_evidence",
  "list_founders",
  "get_activity",
] as const;

export type McpToolName = (typeof MCP_TOOLS)[number];

/** Tool → required scopes (any listed scope grants access; founder bypasses). */
export const MCP_TOOL_SCOPES: Record<McpToolName, readonly McpScope[]> = {
  get_byoa_context: ["read"],
  get_product_brief: ["read", "products", "quests", "attention"],
  get_struggle_digest: ["read", "products", "quests", "attention"],
  list_attention: ["read", "attention"],
  dismiss_attention: ["write", "attention"],
  snooze_attention: ["write", "attention"],
  resolve_attention: ["write", "attention"],
  list_products: ["read", "products"],
  get_product: ["read", "products"],
  update_product: ["write", "products"],
  get_product_strategy: ["read", "products"],
  update_product_strategy: ["write", "products"],
  list_quest_templates: ["read", "quests"],
  get_quest_template: ["read", "quests"],
  get_progression: ["read", "quests"],
  evaluate_quest_readiness: ["read", "quests", "products"],
  list_capability_manifest: ["read", "quests"],
  list_goals: ["read", "quests"],
  suggest_goal: ["write", "quests"],
  accept_goal: ["write", "quests"],
  update_goal: ["write", "quests"],
  list_quests: ["read", "quests"],
  get_quest_progress: ["read", "quests"],
  suggest_quest: ["write", "quests"],
  accept_quest: ["write", "quests"],
  claim_quest: ["write", "quests"],
  attach_quest_proof: ["write", "quests"],
  submit_proof: ["write", "quests"],
  propose_quest_complete: ["write", "quests"],
  create_quest_from_template: ["write", "quests"],
  update_quest: ["write", "quests"],
  archive_quest: ["write", "quests"],
  get_evidence: ["read", "quests"],
  validate_quest_proof: ["write", "quests"],
  report_progress: ["write", "quests", "tasks"],
  report_blocker: ["write", "quests", "tasks"],
  list_customer_metrics: ["read"],
  get_lifecycle_summary: ["read"],
  query_product_metrics: ["read", "products"],
  list_org_events: ["read", "attention"],
  dismiss_org_event: ["write", "attention"],
  list_channel_jobs: ["read"],
  list_ops_jobs: ["read", "tasks", "quests"],
  get_ops_job: ["read", "tasks", "quests"],
  list_job_executions: ["read", "tasks", "quests"],
  report_job_execution: ["write", "tasks", "quests"],
  get_product_integrations: ["read", "products"],
  get_integration_config: ["read", "products"],
  list_provider_resources: ["read", "products"],
  update_product_integration: ["write", "products"],
  request_integration_sync: ["write", "products"],
  get_sync_status: ["read", "products"],
  list_tasks: ["read", "tasks"],
  claim_task: ["write", "tasks"],
  update_task: ["write", "tasks"],
  update_task_status: ["write", "tasks"],
  attach_evidence: ["write", "tasks", "quests"],
  list_founders: ["read", "community"],
  get_activity: ["read", "community"],
};

/** Lookout curve unit — historical flat award; level calc still uses this. */
export const LOOKOUT_MAX_LEVEL = 60;
export const XP_PER_QUEST = 40;

export function lookoutLevelFromXp(xp: number): number {
  return Math.min(
    LOOKOUT_MAX_LEVEL,
    Math.max(1, Math.floor(xp / XP_PER_QUEST) + 1),
  );
}

export function xpFloorForLevel(level: number): number {
  const clamped = Math.min(LOOKOUT_MAX_LEVEL, Math.max(1, level));
  return (clamped - 1) * XP_PER_QUEST;
}

export function lookoutXpProgress(xp: number): {
  level: number;
  intoLevel: number;
  span: number;
  pct: number;
  nextLevel: number | null;
  xpToNext: number;
} {
  const level = lookoutLevelFromXp(xp);
  if (level >= LOOKOUT_MAX_LEVEL) {
    return {
      level,
      intoLevel: 0,
      span: XP_PER_QUEST,
      pct: 100,
      nextLevel: null,
      xpToNext: 0,
    };
  }
  const floor = xpFloorForLevel(level);
  const ceil = xpFloorForLevel(level + 1);
  const span = Math.max(1, ceil - floor);
  const intoLevel = Math.min(span, Math.max(0, xp - floor));
  return {
    level,
    intoLevel,
    span,
    pct: Math.round((intoLevel / span) * 100),
    nextLevel: level + 1,
    xpToNext: Math.max(0, ceil - xp),
  };
}

export const XP_COMPLEXITY_BASE: Record<1 | 2 | 3 | 4 | 5, number> = {
  1: 25,
  2: 60,
  3: 100,
  4: 160,
  5: 240,
};

export const XP_RULE_VERSION_H1 = "h1.v1";
export const XP_RULE_VERSION_H2 = "h2.v1";

export function awardAmountForTemplate(input: {
  executionXp: number;
  evidenceBonusXp: number;
}): number {
  const raw =
    Math.max(0, Number(input.executionXp) || 0) +
    Math.max(0, Number(input.evidenceBonusXp) || 0);
  return Math.min(400, Math.max(0, Math.round(raw)));
}

/**
 * MVP connectors (product signup SoT is separate — not a card).
 * First live slice: buffer + ga + gsc.
 */
export const INTEGRATION_PROVIDERS = [
  "stripe",
  "ga",
  "gsc",
  "instantly",
  "posthog",
  "buffer",
  "ahrefs",
] as const;

export type IntegrationProvider = (typeof INTEGRATION_PROVIDERS)[number];

export type IntegrationStatus =
  | "connected"
  | "disconnected"
  | "stale"
  | "error";

export interface IntegrationConnection {
  id: string;
  organizationId: string;
  productId: string | null;
  provider: IntegrationProvider;
  status: IntegrationStatus;
  metaJson: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
}

export const INTEGRATION_PROVIDER_LABELS: Record<IntegrationProvider, string> = {
  stripe: "Stripe",
  ga: "Google Analytics",
  gsc: "Search Console",
  instantly: "Instantly",
  posthog: "PostHog",
  buffer: "Buffer",
  ahrefs: "Ahrefs",
};

/** Providers with live connect/sync wired (others stay stub/disconnected). */
export const LIVE_INTEGRATION_PROVIDERS = [
  "buffer",
  "instantly",
  "ga",
  "gsc",
  "ahrefs",
  "posthog",
] as const;

export type LiveIntegrationProvider =
  (typeof LIVE_INTEGRATION_PROVIDERS)[number];
